/**
 * NOTE: This file is imported by web-app/packages/omega/src/js/links.js and is
 * meant to be a lightweight way of importing routes to all the omega web-apps.
 * Side effects such as importing model configs or large dependencies are strongly
 * discouraged since it will affect all omega web-apps.
 */
import { get } from 'lodash';
import { hasPermission } from 'utils/user-utils';

export const tridentCampaigns = {
  localeSelector: 'trident_campaign',
  name: 'trident_campaign',
  permissionKey: 'new_trident',
  webPath: '/campaigns',
  to: '/campaigns',
  hasFieldsAccessFunc: (userPermissions, action) => {
    const newTridentAuthorizations = get(
      userPermissions,
      'authorizations.new_trident',
      {}
    );

    return Object.keys(newTridentAuthorizations).some(field =>
      hasPermission(userPermissions, 'new_trident', field, action)
    );
  },
  namespace: 'rtc',
};

export const ucdCampaigns = {
  localeSelector: 'ucd_campaign',
  permissionKey: 'ucd_campaign',
  name: 'ucd_campaign',
  webPath: '/ucd',
  to: '/ucd',
  hasFieldsAccessFunc: (userPermissions, action) => {
    const ucdAuthorizations = get(
      userPermissions,
      'authorizations.ucd_campaign',
      {}
    );

    return Object.keys(ucdAuthorizations).some(field =>
      hasPermission(userPermissions, 'ucd_campaign', field, action)
    );
  },
  namespace: 'rtc',
};

export const debuggingTools = {
  localeSelector: 'debugging_tools',
  name: 'debugging_tools',
  webPath: '/debugging-tools',
  to: '/debugging-tools',
  hasFieldsAccessFunc: (userPermissions, action) => {
    const ucdAuthorizations = get(
      userPermissions,
      'authorizations.ucd_campaign',
      {}
    );

    return Object.keys(ucdAuthorizations).some(field =>
      hasPermission(userPermissions, 'ucd_campaign', field, action)
    );
  },
  namespace: 'rtc',
};

const BASE_LINKS = [tridentCampaigns, ucdCampaigns];

const normalizeEmail = email =>
  String(email || '')
    .trim()
    .toLowerCase();

export const isDebuggingToolVisible = userEmail => {
  const enabledDebuggingToolToAllUsers = get(
    window,
    'Grab.config.PORTAL_CONFIG.holos.enabledDebuggingToolToAllUsers',
    false,
  );

  if (enabledDebuggingToolToAllUsers) {
    return true;
  }

  const whitelistedUsers = get(
    window,
    'Grab.config.PORTAL_CONFIG.holos.debuggingToolWhitelistedUsers',
    [],
  );
  const normalizedUserEmail = normalizeEmail(userEmail);

  if (!normalizedUserEmail || !Array.isArray(whitelistedUsers)) {
    return false;
  }

  return whitelistedUsers.some(
    whitelistedUser =>
      normalizeEmail(whitelistedUser) === normalizedUserEmail,
  );
};

/**
 * Returns an array of routes that can be mapped to <Link to={} /> or <a href={} />.
 * Takes in permissions to filter out unauthorized endpoints.
 *
 * @param {Object} permissions User permission from redux store
 * @param {String | undefined} namespace App namespace
 * @param {String | undefined} userEmail Current user email
 */
export const getSidebarLinks = (
  permissions = {},
  namespace,
  userEmail,
) => {
  const orderedLinks = isDebuggingToolVisible(userEmail)
    ? [...BASE_LINKS, debuggingTools]
    : BASE_LINKS;

  return orderedLinks.filter(link => {
    // Show relevant sidebar links during development
    if (process.env.NODE_ENV === 'development') {
      return link.namespace === namespace;
    }

    if (link.hasFieldsAccessFunc) {
      return link.hasFieldsAccessFunc(permissions, 'read');
    }

    const resourceKey = link.permissionKey || link.name;

    return hasPermission(permissions, resourceKey, '*', 'read');
  });
};
